/**
 * Contract onboarding wizard — 5-step guided flow.
 *
 * SPEC §9: Two entry points (new from scratch, duplicate existing).
 * This component handles "new from scratch" as a full-page route.
 *
 * Steps:
 *  1. Organisation — new or pick existing
 *  2. Contract type & terms — SLA / Warranty / BAU / Internal
 *  3. Integrations — account owner, Harvest, Slack
 *  4. People — invite client users (skippable)
 *  5. Review & activate — summary, final submit
 */
import { useState, useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  FileSignature,
  Link2,
  Users,
  CheckCircle2,
  Plus,
  X,
  Trash2,
} from 'lucide-react';

import { beaconClient } from '@/lib/api/client.js';
import { useAssignableUsers } from '@/hooks/use-users.js';
import { useCreateContract } from '@/hooks/use-contracts.js';
import { useCreateOrganization } from '@/hooks/use-organizations.js';
import { useCreateUser } from '@/hooks/use-users.js';
import { useOrganizations } from '@/hooks/use-organizations.js';

// ---- Types ----

type ContractType = 'sla' | 'warranty' | 'bau' | 'internal';

interface WizardState {
  // Step 1: Organisation
  orgMode: 'existing' | 'new';
  existingOrgId: string;
  newOrgName: string;
  newOrgDomains: string[];
  newOrgContactName: string;
  newOrgContactEmail: string;

  // Step 2: Contract type & terms
  contractType: ContractType;
  contractName: string;
  startDate: string;
  endDate: string;
  // SLA + Warranty
  monthlyHours: string;
  overageRateAud: string;
  responseMinutes: string;
  solutionMinutes: string;
  businessHoursOnly: boolean;
  pauseOutsideHours: boolean;
  // BAU
  totalHours: string;
  durationMonths: string;
  topUpsAllowed: boolean;
  topUpRateAud: string;

  // Step 3: Integrations
  accountOwnerUserId: string;
  harvestProjectId: string;
  slackWebhookUrl: string;

  // Step 4: People
  invites: Array<{ email: string; role: string }>;
}

type Action =
  | { type: 'SET'; field: keyof WizardState; value: unknown }
  | { type: 'ADD_INVITE' }
  | { type: 'REMOVE_INVITE'; index: number }
  | { type: 'SET_INVITE'; index: number; field: 'email' | 'role'; value: string }
  | { type: 'ADD_DOMAIN'; domain: string }
  | { type: 'REMOVE_DOMAIN'; index: number };

const INITIAL_STATE: WizardState = {
  orgMode: 'new',
  existingOrgId: '',
  newOrgName: '',
  newOrgDomains: [],
  newOrgContactName: '',
  newOrgContactEmail: '',
  contractType: 'sla',
  contractName: '',
  startDate: '',
  endDate: '',
  monthlyHours: '',
  overageRateAud: '',
  responseMinutes: '60',
  solutionMinutes: '480',
  businessHoursOnly: true,
  pauseOutsideHours: true,
  totalHours: '',
  durationMonths: '12',
  topUpsAllowed: true,
  topUpRateAud: '',
  accountOwnerUserId: '',
  harvestProjectId: '',
  slackWebhookUrl: '',
  invites: [],
};

function reducer(state: WizardState, action: Action): WizardState {
  switch (action.type) {
    case 'SET':
      return { ...state, [action.field]: action.value };
    case 'ADD_INVITE':
      return { ...state, invites: [...state.invites, { email: '', role: 'client_submitter' }] };
    case 'REMOVE_INVITE':
      return { ...state, invites: state.invites.filter((_, i) => i !== action.index) };
    case 'SET_INVITE': {
      const invites = [...state.invites];
      invites[action.index] = { ...invites[action.index]!, [action.field]: action.value };
      return { ...state, invites };
    }
    case 'ADD_DOMAIN':
      return { ...state, newOrgDomains: [...state.newOrgDomains, action.domain] };
    case 'REMOVE_DOMAIN':
      return { ...state, newOrgDomains: state.newOrgDomains.filter((_, i) => i !== action.index) };
    default:
      return state;
  }
}

// ---- Steps ----

const STEPS = [
  { id: 'org', label: 'Organisation', icon: Building2 },
  { id: 'terms', label: 'Type & Terms', icon: FileSignature },
  { id: 'integrations', label: 'Integrations', icon: Link2 },
  { id: 'people', label: 'People', icon: Users },
  { id: 'review', label: 'Review', icon: CheckCircle2 },
] as const;

// ---- Main Component ----

export default function ContractWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);

  const orgsQuery = useOrganizations();
  const usersQuery = useAssignableUsers();
  const createContract = useCreateContract();
  const createOrg = useCreateOrganization();
  const createUser = useCreateUser();

  const set = useCallback(
    (field: keyof WizardState, value: unknown) =>
      dispatch({ type: 'SET', field, value }),
    []
  );

  const orgs = orgsQuery.data ?? [];
  const users = usersQuery.data ?? [];

  function canAdvance(): boolean {
    if (step === 0) {
      if (state.orgMode === 'existing') return Boolean(state.existingOrgId);
      return Boolean(state.newOrgName.trim());
    }
    if (step === 1) {
      if (!state.contractName.trim() || !state.startDate || !state.endDate) return false;
      if (state.contractType === 'sla' || state.contractType === 'warranty') {
        return Boolean(state.monthlyHours && state.responseMinutes && state.solutionMinutes);
      }
      if (state.contractType === 'bau') {
        return Boolean(state.totalHours && state.durationMonths);
      }
      return true;
    }
    if (step === 2) return Boolean(state.accountOwnerUserId);
    return true; // steps 3 & 4 always advanceable
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      // Step 1: Create org if new
      let orgId = state.existingOrgId;
      if (state.orgMode === 'new') {
        const orgBody = await beaconClient.post<{ id: string }>(
          '/api/organizations',
          {
            name: state.newOrgName.trim(),
            type: 'client',
            primaryContactName: state.newOrgContactName.trim() || undefined,
            primaryContactEmail: state.newOrgContactEmail.trim() || undefined,
            domains: state.newOrgDomains.length > 0 ? state.newOrgDomains : undefined,
          }
        );
        orgId = orgBody.id;
      }

      // Step 2: Create contract
      const isSlaLike = state.contractType === 'sla' || state.contractType === 'warranty';
      const baseInput = {
        organizationId: orgId,
        name: state.contractName.trim(),
        startDate: state.startDate,
        endDate: state.endDate,
        accountOwnerUserId: state.accountOwnerUserId,
        slackChannel: state.slackWebhookUrl.trim() || null,
        harvestProjectId: state.harvestProjectId.trim() || null,
      };

      let contractPayload: Record<string, unknown>;
      if (isSlaLike) {
        const overageCents =
          state.contractType === 'warranty'
            ? 0
            : Math.round(parseFloat(state.overageRateAud || '0') * 100);
        contractPayload = {
          type: state.contractType,
          ...baseInput,
          monthlyHours: parseInt(state.monthlyHours, 10),
          overageRateCents: overageCents,
          responseTargetMinutes: parseInt(state.responseMinutes, 10),
          solutionTargetMinutes: parseInt(state.solutionMinutes, 10),
          businessHoursOnly: state.businessHoursOnly,
          pauseOutsideHours: state.pauseOutsideHours,
        };
      } else if (state.contractType === 'bau') {
        const topUpCents = state.topUpsAllowed
          ? Math.round(parseFloat(state.topUpRateAud || '0') * 100) || null
          : null;
        contractPayload = {
          type: 'bau',
          ...baseInput,
          totalHours: parseInt(state.totalHours, 10),
          durationMonths: parseInt(state.durationMonths, 10),
          topUpsAllowed: state.topUpsAllowed,
          topUpRateCents: topUpCents,
        };
      } else {
        contractPayload = { type: 'internal', ...baseInput };
      }

      const contractBody = await beaconClient.post<{ id: string }>(
        '/api/contracts',
        contractPayload
      );

      // Step 3: Invite users (if any)
      const validInvites = state.invites.filter((i) => i.email.trim());
      for (const invite of validInvites) {
        try {
          await beaconClient.post('/api/users', {
            organizationId: orgId,
            email: invite.email.trim(),
            name: invite.email.split('@')[0],
            role: invite.role,
          });
        } catch {
          // Non-fatal — user invite failures don't block contract creation
        }
      }

      toast.success('Contract activated', {
        description: `${state.contractName} is now live.`,
      });
      navigate(`/admin/clients/${orgId}/contracts/${contractBody.id}`);
    } catch (err) {
      toast.error('Activation failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <header className="px-6 py-4 border-b border-ink-600 flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/clients')}
          className="text-ink-300 hover:text-ink-100 focus-ring rounded-md p-1"
          aria-label="Back to clients"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-lg font-semibold text-ink-100">New contract</h1>
      </header>

      {/* Step indicator */}
      <nav className="px-6 py-3 border-b border-ink-600 flex items-center gap-1" aria-label="Wizard steps">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isCurrent = i === step;
          const isDone = i < step;
          return (
            <button
              key={s.id}
              onClick={() => i < step && setStep(i)}
              disabled={i > step}
              data-testid={`wizard-step-${s.id}`}
              className={
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] transition-colors ' +
                (isCurrent
                  ? 'bg-beacon-400/10 text-beacon-300 font-medium'
                  : isDone
                    ? 'text-ink-200 hover:text-ink-100 cursor-pointer'
                    : 'text-ink-400 cursor-default')
              }
            >
              <Icon size={14} />
              <span className="hidden sm:inline">{s.label}</span>
              {i < STEPS.length - 1 && (
                <span className="text-ink-500 ml-1 hidden sm:inline">›</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Step content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-2xl mx-auto space-y-5">
          {step === 0 && <StepOrg state={state} set={set} dispatch={dispatch} orgs={orgs} />}
          {step === 1 && <StepTerms state={state} set={set} />}
          {step === 2 && <StepIntegrations state={state} set={set} users={users} />}
          {step === 3 && <StepPeople state={state} dispatch={dispatch} />}
          {step === 4 && <StepReview state={state} orgs={orgs} users={users} />}
        </div>
      </div>

      {/* Footer nav */}
      <footer className="px-6 py-3 border-t border-ink-600 flex items-center justify-between">
        <button
          onClick={() => (step === 0 ? navigate('/admin/clients') : setStep(step - 1))}
          disabled={submitting}
          className="flex items-center gap-1.5 h-8 px-4 rounded-md text-[13px] text-ink-200 hover:text-ink-100 bg-ink-800 border border-ink-600 focus-ring disabled:opacity-50"
        >
          <ArrowLeft size={14} />
          {step === 0 ? 'Cancel' : 'Back'}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canAdvance()}
            data-testid="wizard-next"
            className="flex items-center gap-1.5 h-8 px-4 rounded-md text-[13px] font-medium text-ink-900 bg-beacon-400 hover:bg-beacon-300 focus-ring disabled:opacity-40"
          >
            Next
            <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="wizard-activate"
            className="flex items-center gap-1.5 h-8 px-5 rounded-md text-[13px] font-medium text-ink-900 bg-beacon-400 hover:bg-beacon-300 focus-ring disabled:opacity-40"
          >
            {submitting ? 'Activating…' : 'Activate contract'}
          </button>
        )}
      </footer>
    </div>
  );
}

// ---- Step Components ----

function StepOrg({
  state,
  set,
  dispatch,
  orgs,
}: {
  state: WizardState;
  set: (f: keyof WizardState, v: unknown) => void;
  dispatch: React.Dispatch<Action>;
  orgs: Array<{ id: string; name: string; type: string }>;
}) {
  const [domainDraft, setDomainDraft] = useState('');
  const clientOrgs = orgs.filter((o) => o.type === 'client');

  function commitDomain() {
    const d = domainDraft.trim().toLowerCase();
    if (d && !state.newOrgDomains.includes(d)) {
      dispatch({ type: 'ADD_DOMAIN', domain: d });
    }
    setDomainDraft('');
  }

  return (
    <>
      <h2 className="text-[15px] font-semibold text-ink-100">Organisation</h2>
      <p className="text-[13px] text-ink-300 -mt-3">
        Which client is this contract for?
      </p>

      <div className="flex gap-2">
        {(['existing', 'new'] as const).map((m) => (
          <button
            key={m}
            onClick={() => set('orgMode', m)}
            data-testid={`wizard-org-${m}`}
            className={
              'h-8 px-4 rounded-md text-[12.5px] border focus-ring ' +
              (state.orgMode === m
                ? 'bg-ink-700 border-ink-500 text-ink-100'
                : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')
            }
          >
            {m === 'existing' ? 'Existing client' : 'New client'}
          </button>
        ))}
      </div>

      {state.orgMode === 'existing' ? (
        <Field label="Organisation">
          <select
            value={state.existingOrgId}
            onChange={(e) => set('existingOrgId', e.target.value)}
            data-testid="wizard-org-select"
            className="w-full h-9 px-3 rounded-md text-[13px] bg-ink-800 border border-ink-600 text-ink-100 focus-ring"
          >
            <option value="">Select an organisation…</option>
            {clientOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <div className="space-y-3">
          <Field label="Organisation name *">
            <Input
              value={state.newOrgName}
              onChange={(e) => set('newOrgName', e.target.value)}
              placeholder="Acme Pty Ltd"
              data-testid="wizard-org-name"
            />
          </Field>
          <Field label="Email domains (for inbound routing)">
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {state.newOrgDomains.map((d, i) => (
                <span
                  key={d}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded bg-ink-700 text-[12px] text-ink-200"
                >
                  {d}
                  <button
                    onClick={() => dispatch({ type: 'REMOVE_DOMAIN', index: i })}
                    className="text-ink-400 hover:text-ink-200"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <Input
              value={domainDraft}
              onChange={(e) => setDomainDraft(e.target.value)}
              placeholder="acme.com.au"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitDomain();
                }
              }}
              onBlur={commitDomain}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary contact name">
              <Input
                value={state.newOrgContactName}
                onChange={(e) => set('newOrgContactName', e.target.value)}
                placeholder="Jane Smith"
              />
            </Field>
            <Field label="Primary contact email">
              <Input
                type="email"
                value={state.newOrgContactEmail}
                onChange={(e) => set('newOrgContactEmail', e.target.value)}
                placeholder="jane@acme.com.au"
              />
            </Field>
          </div>
        </div>
      )}
    </>
  );
}

function StepTerms({
  state,
  set,
}: {
  state: WizardState;
  set: (f: keyof WizardState, v: unknown) => void;
}) {
  const isSlaLike = state.contractType === 'sla' || state.contractType === 'warranty';

  return (
    <>
      <h2 className="text-[15px] font-semibold text-ink-100">Contract type & terms</h2>

      <Field label="Type">
        <div className="flex gap-1.5">
          {(['sla', 'warranty', 'bau', 'internal'] as const).map((t) => (
            <button
              key={t}
              onClick={() => set('contractType', t)}
              data-testid={`wizard-type-${t}`}
              className={
                'h-7 px-3 rounded-md text-[12.5px] focus-ring border ' +
                (state.contractType === t
                  ? 'bg-ink-700 border-ink-500 text-ink-100'
                  : 'bg-ink-800 border-ink-600 text-ink-300 hover:text-ink-100')
              }
            >
              {t.toUpperCase()}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Contract name *">
        <Input
          value={state.contractName}
          onChange={(e) => set('contractName', e.target.value)}
          placeholder={`${state.contractType === 'warranty' ? 'Post-Build Warranty' : state.contractType.toUpperCase() + ' Support'}`}
          data-testid="wizard-name"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Start date *">
          <Input
            type="date"
            value={state.startDate}
            onChange={(e) => set('startDate', e.target.value)}
            data-testid="wizard-start"
          />
        </Field>
        <Field label="End date *">
          <Input
            type="date"
            value={state.endDate}
            onChange={(e) => set('endDate', e.target.value)}
            data-testid="wizard-end"
          />
        </Field>
      </div>

      {isSlaLike && (
        <fieldset className="rounded-md border border-ink-600 p-3 space-y-3">
          <legend className="px-1.5 text-[11px] uppercase tracking-[0.06em] text-ink-300">
            {state.contractType === 'warranty' ? 'Warranty terms' : 'SLA terms'}
          </legend>
          <div className={`grid ${state.contractType === 'sla' ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
            <Field label="Monthly hours *">
              <Input
                type="number"
                min="1"
                value={state.monthlyHours}
                onChange={(e) => set('monthlyHours', e.target.value)}
                data-testid="wizard-monthly-hours"
              />
            </Field>
            {state.contractType === 'sla' && (
              <Field label="Overage rate A$/h">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={state.overageRateAud}
                  onChange={(e) => set('overageRateAud', e.target.value)}
                />
              </Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Response target (min) *">
              <Input
                type="number"
                min="1"
                value={state.responseMinutes}
                onChange={(e) => set('responseMinutes', e.target.value)}
                data-testid="wizard-response"
              />
            </Field>
            <Field label="Solution target (min) *">
              <Input
                type="number"
                min="1"
                value={state.solutionMinutes}
                onChange={(e) => set('solutionMinutes', e.target.value)}
                data-testid="wizard-solution"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Toggle
              label="Business hours only"
              checked={state.businessHoursOnly}
              onChange={(v) => set('businessHoursOnly', v)}
            />
            <Toggle
              label="Pause outside hours"
              checked={state.pauseOutsideHours}
              onChange={(v) => set('pauseOutsideHours', v)}
            />
          </div>
        </fieldset>
      )}

      {state.contractType === 'bau' && (
        <fieldset className="rounded-md border border-ink-600 p-3 space-y-3">
          <legend className="px-1.5 text-[11px] uppercase tracking-[0.06em] text-ink-300">
            BAU terms
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total hours *">
              <Input
                type="number"
                min="1"
                value={state.totalHours}
                onChange={(e) => set('totalHours', e.target.value)}
                data-testid="wizard-total-hours"
              />
            </Field>
            <Field label="Duration (months) *">
              <Input
                type="number"
                min="1"
                value={state.durationMonths}
                onChange={(e) => set('durationMonths', e.target.value)}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Toggle
              label="Top-ups allowed"
              checked={state.topUpsAllowed}
              onChange={(v) => set('topUpsAllowed', v)}
            />
            {state.topUpsAllowed && (
              <Field label="Top-up rate A$/h">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={state.topUpRateAud}
                  onChange={(e) => set('topUpRateAud', e.target.value)}
                />
              </Field>
            )}
          </div>
        </fieldset>
      )}
    </>
  );
}

function StepIntegrations({
  state,
  set,
  users,
}: {
  state: WizardState;
  set: (f: keyof WizardState, v: unknown) => void;
  users: Array<{ id: string; name: string }>;
}) {
  return (
    <>
      <h2 className="text-[15px] font-semibold text-ink-100">Integrations</h2>

      <Field label="Account owner *">
        <select
          value={state.accountOwnerUserId}
          onChange={(e) => set('accountOwnerUserId', e.target.value)}
          data-testid="wizard-owner"
          className="w-full h-9 px-3 rounded-md text-[13px] bg-ink-800 border border-ink-600 text-ink-100 focus-ring"
        >
          <option value="">Select staff member…</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Harvest project ID">
        <Input
          value={state.harvestProjectId}
          onChange={(e) => set('harvestProjectId', e.target.value)}
          placeholder="Optional — links time entries"
        />
      </Field>

      <Field label="Slack webhook URL">
        <Input
          type="url"
          value={state.slackWebhookUrl}
          onChange={(e) => set('slackWebhookUrl', e.target.value)}
          placeholder="https://hooks.slack.com/services/..."
        />
      </Field>
    </>
  );
}

function StepPeople({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<Action>;
}) {
  return (
    <>
      <h2 className="text-[15px] font-semibold text-ink-100">Invite client users</h2>
      <p className="text-[13px] text-ink-300 -mt-3">
        Optional — add people who should have portal access. You can always do this later.
      </p>

      {state.invites.map((invite, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            type="email"
            value={invite.email}
            onChange={(e) =>
              dispatch({ type: 'SET_INVITE', index: i, field: 'email', value: e.target.value })
            }
            placeholder="jane@acme.com.au"
            className="flex-1"
          />
          <select
            value={invite.role}
            onChange={(e) =>
              dispatch({ type: 'SET_INVITE', index: i, field: 'role', value: e.target.value })
            }
            className="h-9 px-2 rounded-md text-[12.5px] bg-ink-800 border border-ink-600 text-ink-200 focus-ring"
          >
            <option value="client_admin">Admin</option>
            <option value="client_submitter">Submitter</option>
            <option value="client_viewer">Viewer</option>
          </select>
          <button
            onClick={() => dispatch({ type: 'REMOVE_INVITE', index: i })}
            className="text-ink-400 hover:text-danger-400 p-1"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}

      <button
        onClick={() => dispatch({ type: 'ADD_INVITE' })}
        data-testid="wizard-add-invite"
        className="flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] text-ink-200 hover:text-ink-100 bg-ink-800 border border-ink-600 border-dashed focus-ring"
      >
        <Plus size={14} />
        Add person
      </button>
    </>
  );
}

function StepReview({
  state,
  orgs,
  users,
}: {
  state: WizardState;
  orgs: Array<{ id: string; name: string }>;
  users: Array<{ id: string; name: string }>;
}) {
  const orgName =
    state.orgMode === 'existing'
      ? orgs.find((o) => o.id === state.existingOrgId)?.name ?? '—'
      : state.newOrgName || '—';
  const ownerName =
    users.find((u) => u.id === state.accountOwnerUserId)?.name ?? '—';

  return (
    <>
      <h2 className="text-[15px] font-semibold text-ink-100">Review & activate</h2>

      <div className="rounded-md border border-ink-600 divide-y divide-ink-600">
        <ReviewRow label="Organisation" value={orgName} />
        {state.orgMode === 'new' && state.newOrgDomains.length > 0 && (
          <ReviewRow label="Domains" value={state.newOrgDomains.join(', ')} />
        )}
        <ReviewRow label="Contract type" value={state.contractType.toUpperCase()} />
        <ReviewRow label="Contract name" value={state.contractName} />
        <ReviewRow label="Period" value={`${state.startDate} → ${state.endDate}`} />
        <ReviewRow label="Account owner" value={ownerName} />

        {(state.contractType === 'sla' || state.contractType === 'warranty') && (
          <>
            <ReviewRow label="Monthly hours" value={state.monthlyHours} />
            <ReviewRow
              label="Response / Solution"
              value={`${state.responseMinutes} min / ${state.solutionMinutes} min`}
            />
            {state.contractType === 'sla' && state.overageRateAud && (
              <ReviewRow label="Overage rate" value={`A$${state.overageRateAud}/h`} />
            )}
          </>
        )}

        {state.contractType === 'bau' && (
          <>
            <ReviewRow label="Total hours" value={state.totalHours} />
            <ReviewRow label="Duration" value={`${state.durationMonths} months`} />
          </>
        )}

        {state.invites.filter((i) => i.email.trim()).length > 0 && (
          <ReviewRow
            label="Invites"
            value={state.invites
              .filter((i) => i.email.trim())
              .map((i) => `${i.email} (${i.role})`)
              .join(', ')}
          />
        )}
      </div>
    </>
  );
}

// ---- Shared primitives ----

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-[12.5px] text-ink-300">{label}</span>
      <span className="text-[13px] text-ink-100 font-medium mono">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.06em] text-ink-300 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={
        'w-full h-9 px-3 rounded-md text-[13px] bg-ink-800 border border-ink-600 text-ink-100 placeholder:text-ink-500 focus-ring ' +
        (props.className ?? '')
      }
    />
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="rounded border-ink-500 bg-ink-800 text-beacon-400 focus:ring-beacon-400/30 focus:ring-offset-0"
      />
      <span className="text-[12.5px] text-ink-200">{label}</span>
    </label>
  );
}
